/**
 * TanStack Query factories for the signed-in user's own profile.
 *
 * Used by the /settings page (Phase 7 R16). Profiles RLS already lets
 * users read + update their own row, so no special wrappers are needed.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export type FootSizeUnit = 'eu' | 'uk' | 'us';

export interface ProfileRow {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  language_preference: string;
  timezone: string;
  foot_size_unit: FootSizeUnit;
}

export const profileKeys = {
  all: ['profile'] as const,
  self: () => ['profile', 'self'] as const,
};

export function selfProfileQuery(client: SupabaseClient<Database>, userId: string | null) {
  return {
    queryKey: profileKeys.self(),
    queryFn: async (): Promise<ProfileRow | null> => {
      if (!userId) return null;
      // See updateSelfProfile() — same type-lag workaround. The select
      // string is correct against the live schema; the cast bridges
      // the types until db:gen-types is re-run after migration deploys.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const SELECT = 'id, full_name, email, phone, avatar_url, language_preference, timezone, foot_size_unit' as any;
      const { data, error } = await client
        .from('profiles')
        .select(SELECT)
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as ProfileRow | null) ?? null;
    },
    enabled: !!userId,
  };
}

export interface ProfileUpdate {
  full_name?: string;
  phone?: string | null;
  avatar_url?: string | null;
  language_preference?: string;
  timezone?: string;
  foot_size_unit?: FootSizeUnit;
}

export async function updateSelfProfile(
  client: SupabaseClient<Database>,
  userId: string,
  patch: ProfileUpdate,
): Promise<void> {
  // The generated `Database` types lag the live schema until
  // `pnpm db:gen-types` runs after the Phase 7 settings migration deploys.
  // Casting through `unknown` keeps callers correctly typed while letting
  // the new `timezone` / `foot_size_unit` columns through.
  const { error } = await client
    .from('profiles')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update(patch as unknown as any)
    .eq('id', userId);
  if (error) throw error;
}

/**
 * Upload an avatar image to the `avatars` bucket. Convention: the path
 * is `{user_id}/avatar.{ext}` so each user has at most one canonical
 * avatar file (cache-busted via timestamp on read). Returns the public
 * URL ready to write into `profiles.avatar_url`.
 */
export async function uploadAvatar(
  client: SupabaseClient<Database>,
  userId: string,
  file: File,
): Promise<string> {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `${userId}/avatar.${ext}`;
  const { error: upErr } = await client.storage
    .from('avatars')
    .upload(path, file, { upsert: true, cacheControl: '60', contentType: file.type });
  if (upErr) throw upErr;
  const { data } = client.storage.from('avatars').getPublicUrl(path);
  // Cache-bust so the new image renders immediately even if the URL
  // string is byte-identical to the prior one.
  return `${data.publicUrl}?t=${Date.now()}`;
}

/**
 * Update the auth password via Supabase Auth. RLS-irrelevant (this is
 * an Auth API, not a Postgres operation). Returns void on success,
 * throws on failure. Min 8 chars enforced server-side.
 */
export async function updatePassword(
  client: SupabaseClient<Database>,
  newPassword: string,
): Promise<void> {
  const { error } = await client.auth.updateUser({ password: newPassword });
  if (error) throw error;
}
